import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Globe, Activity, Database, Search, Users } from 'lucide-react';
import { CaseStudyHero } from '../components/CaseStudyHero';
import { CaseStudyContent } from '../components/CaseStudyContent';
import { CaseStudySection } from '../components/CaseStudySection';
import { MetricCard } from '../components/MetricCard';
import { ImageGallery } from '../components/ImageGallery';
import { Footer } from '../components/Footer';
import { FeatureModal } from '../components/FeatureModal';
import { motion } from 'framer-motion';
import { CaseStudyTableOfContents } from '../components/CaseStudyTableOfContents';
// Placeholder features for Ginkgo - to be updated with actual content from portfolio PNGs
const features = [{
  id: 1,
  title: 'Streamlined Traveler Registration',
  description: 'A fast, mobile-friendly registration experience that replaced lengthy paper forms and increased participation rates.',
  image: 'https://cdn.magicpatterns.com/uploads/vCC3CsTJgm4uLxQ1EiLyRh/ginkgo-ipad.png',
  modalImage: 'https://cdn.magicpatterns.com/uploads/8nzPUpY6fEJ9Tj6GKd9Hsk/ginkgo-registrationbig.png',
  outcomeBadge: 'Registration under 60s',
  userInsight: 'Travelers were deterred by a lengthy paper form that took too long to complete, leading many to abandon the registration process entirely.',
  solution: 'A streamlined, mobile-first digital registration flow that reduced the process from 15 fields to 5 essential inputs, with smart defaults and QR code scanning.',
  userOutcome: 'Travelers can register in under 60 seconds, leading to significantly higher participation and a more positive experience at the testing site.',
  businessOutcome: 'Traveler participation rates increased dramatically, providing a larger and more representative data set for pathogen surveillance.',
  quote: ''
}, {
  id: 2,
  title: 'Real-Time Clinician Data Entry',
  description: 'A purpose-built tool for clinical staff to register samples as they go, eliminating end-of-day manual data entry.',
  image: 'https://cdn.magicpatterns.com/uploads/nSqRchnR6w8TkiUDa7JJNm/ginkgo-clinician-sm.png',
  modalImage: 'https://cdn.magicpatterns.com/uploads/aTaszaeUWhJnJhrRdqdmiF/ginkgo-clinician-big.png',
  outcomeBadge: 'Near-zero errors',
  userInsight: 'Clinical staff found the existing sample registration tool too cumbersome for the fast pace of airport testing, resorting to writing barcodes on clipboards and entering data manually at the end of long shifts.',
  solution: 'A streamlined, scan-first interface optimized for speed and accuracy, allowing clinicians to register samples in real time with barcode scanning and minimal manual input.',
  userOutcome: 'Clinicians can register samples on the spot in seconds, eliminating the backlog of manual data entry and reducing errors from transcription.',
  businessOutcome: 'Sample processing throughput increased significantly, and data accuracy improved by eliminating end-of-day manual entry errors.',
  quote: "I used to dread the end of my shift because I knew I had an hour of data entry ahead of me. Now it's done as I go."
},
// PRESERVED FOR FUTURE CASE STUDY:
// {
//   id: 3,
//   title: 'Scientific Exploration & Discovery',
//   description:
//     'Interactive tools for bioinformaticians and epidemiologists to explore genomic sequencing data and identify emerging threats.',
//   image:
//     'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?q=80&w=2676&auto=format&fit=crop',
//   outcomeBadge: 'Faster threat detection',
//   userInsight:
//     'Scientists needed to explore complex genomic data to identify novel pathogens and track mutations, but existing tools required extensive manual data manipulation and lacked visual exploration capabilities.',
//   solution:
//     'An interactive data exploration environment with filterable visualizations, phylogenetic tree views, and the ability to drill down from high-level trends to individual sample-level genomic data.',
//   userOutcome:
//     'Scientists can rapidly explore and cross-reference sequencing results, accelerating the identification of novel variants and emerging biological threats.',
//   businessOutcome:
//     'Enabled faster detection of potential biosecurity threats, strengthening the scientific credibility and value proposition of the platform.',
//   quote:
//     'This is the first time I can actually explore the data visually instead of writing custom scripts for every question I have.',
// },
{
  id: 4,
  title: 'Live Program Insights',
  description: 'A live dashboard with up-to-date Covid tracking for CDC leadership and accurate program metrics for operational staff.',
  image: 'https://cdn.magicpatterns.com/uploads/oAuiBcrbYNPWCNuNn3QsbM/ginkgo-analyst-sm.png',
  modalImage: 'https://cdn.magicpatterns.com/uploads/xoeJ5tkSzB71kdGPkLUZau/ginkgo-dashboard.png',
  outcomeBadge: 'Self-service access',
  userInsight: 'CDC executives and program managers lacked a single source of truth for program performance, relying on manually compiled reports that were often outdated by the time they were reviewed.',
  solution: 'A real-time dashboard with configurable views showing key program metrics, geographic coverage, sample volume trends, and pathogen detection alerts.',
  userOutcome: 'All staff and CDC leadership have self-service access to pilot data, presented in straight-forward formats.',
  businessOutcome: 'Improved stakeholder confidence in the program, supporting contract renewals and expansion to additional airports and international partners.',
  quote: "I finally have a clear picture of how the program is performing across all sites. I don't have to chase down reports anymore."
}];
const sections = [{
  id: 'overview',
  title: 'Overview',
  description: 'Building the immune system for the planet'
}, {
  id: 'challenge',
  title: 'The Challenge',
  description: 'Scaling biosecurity in a connected world'
}, {
  id: 'team',
  title: 'The Team',
  description: 'Cross-functional collaboration'
}, {
  id: 'research',
  title: 'Research & Discovery',
  description: 'Understanding the needs of analysts and policymakers'
}, {
  id: 'insights',
  title: 'Key Insights',
  description: 'Cumbersome tools lead to low participation rates and long workdays.'
}, {
  id: 'ideation',
  title: 'Ideation & Planning',
  description: 'Designing for clarity and speed'
}, {
  id: 'solution',
  title: 'The Solution',
  description: 'An integrated biosecurity platform'
}, {
  id: 'results',
  title: 'Results & Impact',
  description: 'Strengthening global health security'
}];
export function GinkgoCaseStudy() {
  const [selectedFeature, setSelectedFeature] = useState<(typeof features)[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openFeature = (feature: (typeof features)[0]) => {
    setSelectedFeature(feature);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const goToPrevious = () => {
    if (!selectedFeature) return;
    const currentIndex = features.findIndex(f => f.id === selectedFeature.id);
    if (currentIndex > 0) {
      setSelectedFeature(features[currentIndex - 1]);
    }
  };
  const goToNext = () => {
    if (!selectedFeature) return;
    const currentIndex = features.findIndex(f => f.id === selectedFeature.id);
    if (currentIndex < features.length - 1) {
      setSelectedFeature(features[currentIndex + 1]);
    }
  };
  const currentIndex = selectedFeature ? features.findIndex(f => f.id === selectedFeature.id) : -1;
  return <div className="min-h-screen bg-white font-sans text-slate-900" data-id="element-379">
      <CaseStudyTableOfContents sections={sections} data-id="element-380" />

      {/* Back Button */}
      <Link to="/" className="fixed top-8 left-8 z-50 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-sm px-4 py-2 text-sm font-medium text-slate-700 shadow-lg transition-all hover:bg-white hover:shadow-xl hover:scale-105" data-id="element-381">
        <ArrowLeft size={16} data-id="element-382" />
        Back to Home
      </Link>

      {/* SECTION 1: HERO IMAGE */}
      <CaseStudyHero image="https://cdn.magicpatterns.com/uploads/f46VvfWAfRPyY2QYsF5U52/ginkgo-hero.jpg" mobileImage="https://cdn.magicpatterns.com/uploads/d3b2YfkhtJNqmnhHcFS7RD/ginkgo-hero-mobile.jpg" data-id="element-383" />

      {/* SECTION 1.5: HERO CONTENT */}
      <CaseStudyContent title="Tracking pathogens across the globe" subtitle="Transforming a national COVID testing program into a robust biosecurity platform tracking 60+ pathogens worldwide" role="Experience Strategist" timeline="2022 - 2024" client="Ginkgo Biosecurity" outcome="Successful platform pivot" data-id="element-384" />

      {/* Stats Bridge Bar - overlaps hero and overview */}
      <div className="relative z-10 mx-auto max-w-4xl px-6 -mt-8 mb-[-3rem]" data-id="element-385">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="bg-white rounded-2xl shadow-xl border border-slate-100 px-6 py-8 md:px-10" data-id="element-386">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4" data-id="element-387">
            {[{
            value: '41%',
            label: 'Increase in traveler participation',
            icon: Users
          }, {
            value: '~0',
            label: 'Error rate in data collection',
            icon: Shield
          }, {
            value: '8+',
            label: 'New airports in program expansion',
            icon: Globe
          }].map((stat, index) => <motion.div key={stat.label} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3 + index * 0.12,
            duration: 0.5
          }} className="flex items-center gap-4 md:justify-center" data-id="element-388">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-389">
                  <stat.icon className="w-5 h-5 text-blue-600" data-id="element-390" />
                </div>
                <div data-id="element-391">
                  <p className="text-2xl md:text-3xl font-bold text-blue-600 leading-none" data-id="element-392">
                    {stat.value}
                  </p>
                  <p className="text-sm text-slate-500 mt-1 leading-snug" data-id="element-393">
                    {stat.label}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      {/* SECTION 2: OVERVIEW/CONTEXT */}
      <CaseStudySection id="overview" title="Overview" subtitle="Building the immune system for the planet." background="white" data-id="element-394">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2" data-id="element-395">
          <div className="space-y-6 text-lg text-slate-600" data-id="element-396">
            <p data-id="element-397">
              Throughout the pandemic, Concentric, a startup operating under
              Ginkgo Bioworks, was a national leader in Covid testing programs.
              Their mission was to keep American schools safe. As the pandemic
              waned, Ginkgo aspired to transform Concentric to meet biological
              threats on a global scale, creating Ginkgo Biosecurity.
            </p>
            <p data-id="element-398">
              I led the design of the core software platform that powers this
              biosecurity infrastructure, beginning with a pilot with the CDC.
              Our goal was to create a system that could ingest vast amounts of
              human samples gathered at national airports, identify potential
              threats, and present actionable insights to public health
              officials.
            </p>
          </div>
          <div className="flex items-center justify-center rounded-2xl bg-blue-50 p-8" data-id="element-399">
            <div className="space-y-4" data-id="element-400">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-401">
                Key Objectives
              </h3>
              <ul className="space-y-3" data-id="element-402">
                {['Increase traveler participation', 'Streamline sample-to-result workflows', 'Enable rapid threat detection', 'Facilitate cross-agency collaboration'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-700" data-id="element-403">
                    <div className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" data-id="element-404" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 3: CHALLENGE */}
      <CaseStudySection id="challenge" title="The Challenge" subtitle="Scaling biosecurity in a connected world." background="gray" data-id="element-405">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-406">
          <div className="space-y-6 text-lg text-slate-600" data-id="element-407">
            <p data-id="element-408">
              Define and deliver a new vision for protecting the health of
              nations. Identify necessary digital touchpoints, transforming
              existing software and designing new solutions as customer needs
              crystallized through a CDC-sponsored pilot program.
            </p>
            <p data-id="element-409">
              The challenge was to design a unified platform that could handle
              this complexity while remaining accessible to a diverse set of
              users, from lab technicians to high-level government officials. We
              needed to balance technical depth with clarity and speed.
            </p>
          </div>

          <div className="relative" data-id="element-410">
            <img src="https://cdn.magicpatterns.com/uploads/4abM2s9N1YBo1Prina44As/ginkgo-world.png" alt="Legacy systems and complex data" className="w-full h-auto rounded-xl shadow-xl" data-id="element-411" />
            <div className="mt-4 text-center" data-id="element-412">
              <span className="text-sm font-medium text-slate-500 uppercase tracking-wider" data-id="element-413">
                Creating a global network of airports to detect the movement of
                pathogens.
              </span>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 3.5: THE TEAM */}
      <CaseStudySection id="team" title="The Team" subtitle="Cross-functional collaboration." background="white" data-id="element-414">
        <div className="space-y-12" data-id="element-415">
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl" data-id="element-416">
            This project required close collaboration between design, product,
            engineering, and scientific stakeholders. I assumed a player-coach
            role, both leading and executing user research and design work
            streams for the three core components of the platform.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-417">
            {/* Core Team */}
            <div data-id="element-418">
              <h3 className="text-xl font-bold text-blue-600 mb-4" data-id="element-419">
                The Core Team:
              </h3>
              <ul className="space-y-2" data-id="element-420">
                <li className="flex items-start gap-2 text-slate-700" data-id="element-421">
                  <span className="text-blue-500 mt-1" data-id="element-422">•</span>
                  <span data-id="element-423">Experience Strategist (myself)</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-424">
                  <span className="text-blue-500 mt-1" data-id="element-425">•</span>
                  <span data-id="element-426">UX Designer</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-427">
                  <span className="text-blue-500 mt-1" data-id="element-428">•</span>
                  <span data-id="element-429">Content Designer</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-430">
                  <span className="text-blue-500 mt-1" data-id="element-431">•</span>
                  <span data-id="element-432">Data Designer</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-433">
                  <span className="text-blue-500 mt-1" data-id="element-434">•</span>
                  <span data-id="element-435">Product Managers</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-436">
                  <span className="text-blue-500 mt-1" data-id="element-437">•</span>
                  <span data-id="element-438">Data Engineers</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-439">
                  <span className="text-blue-500 mt-1" data-id="element-440">•</span>
                  <span data-id="element-441">Software Engineers</span>
                </li>
              </ul>
            </div>

            {/* Stakeholders */}
            <div data-id="element-442">
              <h3 className="text-xl font-bold text-blue-600 mb-4" data-id="element-443">
                Customer Collaborators:
              </h3>
              <ul className="space-y-2" data-id="element-444">
                <li className="flex items-start gap-2 text-slate-700" data-id="element-445">
                  <span className="text-blue-500 mt-1" data-id="element-446">•</span>
                  <span data-id="element-447">CDC Analysts</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-448">
                  <span className="text-blue-500 mt-1" data-id="element-449">•</span>
                  <span data-id="element-450">CDC Executives</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-451">
                  <span className="text-blue-500 mt-1" data-id="element-452">•</span>
                  <span data-id="element-453">Program Managers</span>
                </li>
              </ul>
            </div>

            {/* Subject Matter Experts */}
            <div data-id="element-454">
              <h3 className="text-xl font-bold text-blue-600 mb-4" data-id="element-455">
                Subject Matter Experts:
              </h3>
              <ul className="space-y-2" data-id="element-456">
                <li className="flex items-start gap-2 text-slate-700" data-id="element-457">
                  <span className="text-blue-500 mt-1" data-id="element-458">•</span>
                  <span data-id="element-459">Bioinformaticians</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-460">
                  <span className="text-blue-500 mt-1" data-id="element-461">•</span>
                  <span data-id="element-462">Epidemiologists</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-463">
                  <span className="text-blue-500 mt-1" data-id="element-464">•</span>
                  <span data-id="element-465">Physical Product Engineer</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Iterative Approach Callout */}
          <div className="mt-16 pt-16 border-t border-slate-200" data-id="element-466">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-467">
              <div className="max-w-2xl" data-id="element-468">
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-469">
                  My Approach
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-470">
                  Over the course of 6 months, I combined Design Thinking with
                  an Agile mindset, working iteratively to test and refine
                  solutions with real users and peers across the org.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-471">
                {['Data-driven', 'Nimble', 'Inclusive', 'Experimentative', 'Iterative'].map(tag => <span key={tag} className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-full border border-blue-200" data-id="element-472">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 4: RESEARCH & DISCOVERY */}
      <CaseStudySection id="research" title="Research & Discovery" subtitle="Understanding the needs of travelers, clinicians, and policymakers." activities={['Ethnographic Study', 'Contextual Interviews', 'Stakeholder Interviews', 'SME Learning Sessions']} background="gray" data-id="element-473">
        <div className="space-y-6" data-id="element-474">
          <p className="text-lg text-slate-700 leading-relaxed" data-id="element-475">
            Hit the ground running on an active CDC pilot. Interviewed
            stakeholders and subject matter experts, spent two days at EWR
            airport observing testing sites, and conducted contextual interviews
            with travelers, clinical staff, and operational teams.
          </p>
        </div>

        <ImageGallery layout="single" images={[{
        src: 'https://cdn.magicpatterns.com/uploads/4z9m6P6482o3pvgkFAaYA7/ginkgo-traveler.png',
        alt: 'Traveler providing sample at airport testing site',
        caption: 'Observing the on-the-ground experience of travelers and clinicians revealed key opportunities for efficiency.'
      }]} data-id="element-476" />
      </CaseStudySection>

      {/* SECTION 5: KEY INSIGHTS */}
      <CaseStudySection id="insights" title="Key Insights" subtitle="Cumbersome tools lead to low participation rates and long workdays." background="white" data-id="element-477">
        <div className="space-y-12" data-id="element-478">
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl" data-id="element-479">
            Our research uncovered three primary challenges across the pilot
            workflow.
          </p>

          {/* Insight Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-480">
            {[{
            number: '01',
            persona: 'Travelers',
            challenge: 'Travelers struggled with a lengthy paper form and often abandoned the registration process.',
            color: 'blue'
          }, {
            number: '02',
            persona: 'Clinical Staff',
            challenge: 'Clinical staff found the tool for registering samples too cumbersome for real-time work, often writing barcodes on a clipboard and entering it manually at the end of the workday.',
            color: 'blue'
          }, {
            number: '03',
            persona: 'Health Analysts',
            challenge: 'Health analysts struggled to find meaning in the raw data and created custom tools to extract insights.',
            color: 'blue'
          }].map((insight, index) => <motion.div key={insight.number} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="relative bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-481">
                <div className="absolute -top-4 left-6" data-id="element-482">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white text-sm font-bold" data-id="element-483">
                    {insight.number}
                  </span>
                </div>
                <div className="pt-4" data-id="element-484">
                  <h4 className="text-lg font-bold text-slate-900 mb-3" data-id="element-485">
                    {insight.persona}
                  </h4>
                  <p className="text-slate-600 leading-relaxed" data-id="element-486">
                    {insight.challenge}
                  </p>
                </div>
              </motion.div>)}
          </div>

          {/* Quote */}
          <div className="border-l-4 border-blue-500 bg-blue-50/50 py-4 px-6 rounded-r max-w-3xl" data-id="element-487">
            <p className="text-slate-700 leading-relaxed" data-id="element-488">
              "I don't just need the answer; I need to know *why* it's the
              answer, so I can defend it to my superiors."
            </p>
            <p className="text-sm text-slate-500 italic mt-2" data-id="element-489">
              — Senior Biosecurity Analyst
            </p>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 6: IDEATION */}
      <CaseStudySection id="ideation" title="Ideation & Planning" subtitle="Designing for clarity and speed." activities={['Workshop Facilitation', 'Journey Mapping', 'Ideation Exercises', 'Co-design Sessions', 'Prioritization Exercises']} background="gray" data-id="element-490">
        <div className="space-y-12" data-id="element-491">
          <p className="max-w-3xl text-lg text-slate-600" data-id="element-492">
            Collaborated with CX and PM peers to map the complete airport
            experience, uncovering opportunities across operations, marketing,
            and software. Conducted ideation sessions internally and with CDC
            stakeholders to imagine the future-state. Facilitated workshops with
            leadership to propose and prioritize focus areas based on user pain
            points and business goals.
          </p>

          <ImageGallery layout="grid" images={[{
          src: 'https://cdn.magicpatterns.com/uploads/xbJthBSMeesyaV6FdEk8iQ/ginkgo-mapping.png',
          alt: 'User research mapping showing risk questions and Jobs to Be Done framework',
          caption: 'Planning exercises were grounded in user needs and preferences.'
        }, {
          src: 'https://cdn.magicpatterns.com/uploads/e4QdPrjdqF71Sv9LoexwU2/ginkgo-codesign.png',
          alt: 'Co-design session artifacts with handwritten notes, sticky notes, and CDC dashboard feedback',
          caption: 'Initial design exercises including CDC analysts ensured the final solution would meet their needs.'
        }]} data-id="element-493" />
        </div>
      </CaseStudySection>

      {/* SECTION 7: THE SOLUTION - 2x2 GRID */}
      <CaseStudySection id="solution" title="The Solution" subtitle="Iterating toward an integrated biosecurity platform." activities={['Rapid Prototyping', 'Data Visualization', 'Scientific Review', 'User Feedback', 'Usability Testing']} background="white" data-id="element-494">
        <div className="space-y-12" data-id="element-495">
          <p className="max-w-3xl text-lg text-slate-600 leading-relaxed" data-id="element-496">
            Over the course of 6 months, working in Agile sprints with PM and
            Engineering, I designed and tested a new suite of tools for the CDC
            pilot program.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-497">
            {features.map((feature, index) => <motion.button key={feature.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} onClick={() => openFeature(feature)} className="group relative overflow-hidden rounded-2xl bg-slate-50 border border-slate-200 p-6 text-left transition-all hover:shadow-xl hover:scale-[1.02] hover:border-blue-300" data-id="element-498">
                {/* Thumbnail with badge */}
                <div className="relative mb-4 overflow-hidden rounded-xl bg-white shadow-md" data-id="element-499">
                  <img src={feature.image} alt={feature.title} className="w-full h-48 object-cover object-center transition-transform duration-500 group-hover:scale-105" data-id="element-500" />
                  {/* Outcome Badge */}
                  <span className="absolute top-3 right-3 px-3 py-1 text-xs font-semibold bg-blue-500 text-white rounded-full shadow-md z-10" data-id="element-501">
                    {feature.outcomeBadge}
                  </span>
                </div>

                {/* Title & Description */}
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-502">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm" data-id="element-503">
                  {feature.description}
                </p>

                {/* Persistent Details Link */}
                <div className="mt-4 flex items-center gap-1 text-sm font-medium text-blue-600" data-id="element-504">
                  <span data-id="element-505">Details</span>
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-506">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-507" />
                  </svg>
                </div>

                {/* Animated bottom border */}
                <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-blue-500 group-hover:w-full transition-all duration-500 ease-out" data-id="element-508" />
              </motion.button>)}
          </div>
        </div>
      </CaseStudySection>

      {/* Feature Modal */}
      <FeatureModal feature={selectedFeature} isOpen={isModalOpen} onClose={closeModal} onPrevious={goToPrevious} onNext={goToNext} hasPrevious={currentIndex > 0} hasNext={currentIndex < features.length - 1} data-id="element-509" />

      {/* SECTION 8: RESULTS & IMPACT */}
      <CaseStudySection id="results" title="Results & Impact" subtitle="Strengthening global health security." background="gray" data-id="element-510">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-511">
          <MetricCard icon={Users} gradient="blue" value="41" suffix="%" label="Increase in Traveler Participation" description="Streamlined registration drove significantly higher opt-in rates across all pilot sites." data-id="element-512" />
          <MetricCard icon={Shield} gradient="blue" value="~0" label="Error Rate in Data Collection" description="Real-time clinician tools eliminated manual transcription errors from the workflow." data-id="element-513" />
          <MetricCard icon={Globe} gradient="blue" value="8+" label="New Airports in Program Expansion" description="Successful pilot results supported expansion to additional airports and international partners." data-id="element-514" />
        </div>
      </CaseStudySection>

      <Footer data-id="element-515" />
    </div>;
}